/**
 * Console shell (design: Admin Console › SHELL): collapsible nav, header with breadcrumbs, search (⌘K),
 * "Show data for" scope filter, notifications, theme toggle and user menu.
 */
import { ROLE_LABELS, type NotificationView } from '@uatp/shared';
import {
  Avatar,
  Badge,
  Breadcrumb,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  IconButton,
  Kbd,
  Popover,
  PopoverContent,
  PopoverTrigger,
  cn,
  useTheme,
} from '@uatp/ui';
import logo from '@uatp/ui/logo.png';
import {
  Bell,
  Building2,
  CalendarCheck,
  ChevronDown,
  LayoutDashboard,
  Library,
  Moon,
  PanelLeft,
  Search,
  Server,
  Settings,
  Sparkles,
  Sun,
  Users,
  type LucideIcon,
} from 'lucide-react';
import { Suspense, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { patch, post } from '../api/client';
import { useRealtime } from '../api/realtime';
import { useAuth } from '../auth/auth';
import { AiAssistantDrawer } from '../components/AiAssistantDrawer';
import { PageSpinner } from '../components/states';
import { relTime } from '../lib/format';
import { invalidate, useGet } from '../lib/query';
import { useScope } from '../lib/scope';
import { useSystems } from '../lib/systems';
import { CommandPalette } from './CommandPalette';
import { useShell } from './store';

interface NavDef {
  key: string;
  to?: string;
  label: string;
  icon: LucideIcon;
  match?: string[];
  show?: boolean;
  badge?: number;
  onClick?: () => void;
}

function NavItem({ item, open }: { item: NavDef; open: boolean }) {
  const location = useLocation();
  const active = item.to
    ? (item.match ?? [item.to]).some((m) => (m === '/' ? location.pathname === '/' : location.pathname.startsWith(m)))
    : false;
  const cls = cn(
    'flex h-9 items-center gap-2.5 overflow-hidden whitespace-nowrap rounded-[6px] px-2.5 text-start text-13 transition-colors',
    active ? 'bg-primary-soft font-semibold text-primary-text' : 'font-medium text-text-2 hover:bg-surface-2 hover:text-text',
  );
  const inner = (
    <>
      <item.icon className="size-[18px] shrink-0" aria-hidden />
      {open && <span className="min-w-0 flex-1 truncate">{item.label}</span>}
      {open && !!item.badge && (
        <span className="rounded-full bg-error px-1.5 text-10 font-semibold text-white" aria-label={`${item.badge} new`}>
          {item.badge > 99 ? '99+' : item.badge}
        </span>
      )}
    </>
  );
  if (!item.to) {
    return (
      <button type="button" className={cls} onClick={item.onClick} title={open ? undefined : item.label}>
        {inner}
      </button>
    );
  }
  return (
    <NavLink to={item.to} className={cls} aria-current={active ? 'page' : undefined} title={open ? undefined : item.label}>
      {inner}
    </NavLink>
  );
}

function ScopeMenu() {
  const { me, isGlobal } = useAuth();
  const { systemId, setSystemId } = useScope();
  const { systems: list, data: systemsData } = useSystems();
  const current = list.find((s) => s.id === systemId);

  // A remembered system that is no longer visible resets to "all".
  useEffect(() => {
    if (systemId && systemsData && !systemsData.items.some((s) => s.id === systemId)) setSystemId(null);
  }, [systemId, systemsData, setSystemId]);

  const label = current ? current.name : isGlobal ? `${me.orgName} · all systems` : `My systems (${list.length})`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-[34px] max-w-[200px] shrink-0 items-center gap-1.5 overflow-hidden rounded-md border border-border bg-surface px-2.5 text-13 font-medium text-text hover:bg-surface-2"
          aria-label={`Show data for: ${label}`}
        >
          <Building2 className="size-3.5 shrink-0 text-text-2" aria-hidden />
          <span className="truncate">{label}</span>
          <ChevronDown className="size-3 shrink-0 text-text-3" aria-hidden />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[260px]">
        <DropdownMenuLabel>Show data for</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={systemId ?? 'all'} onValueChange={(v) => setSystemId(v === 'all' ? null : v)}>
          <DropdownMenuRadioItem value="all">
            <span className="flex-1">{isGlobal ? `All systems in ${me.orgName}` : 'All my systems'}</span>
            <span className="text-11 text-text-3">{list.length}</span>
          </DropdownMenuRadioItem>
          {list.map((s) => (
            <DropdownMenuRadioItem key={s.id} value={s.id}>
              <span className="flex-1 truncate">{s.name}</span>
              <span className="text-11 uppercase text-text-3">{s.environment}</span>
            </DropdownMenuRadioItem>
          ))}
        </DropdownMenuRadioGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const NOTIF_TONE: Record<string, string> = {
  feedback_critical: 'bg-critical',
  feedback_high: 'bg-error',
  tester_stalled: 'bg-warning',
  session_ending: 'bg-warning',
  signoff_requested: 'bg-primary',
  signoff_completed: 'bg-success',
  tester_completed: 'bg-success',
  report_ready: 'bg-info',
};

function NotificationsButton() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const q = useGet<{ items: NotificationView[]; unread: number }>('/notifications', { refetchInterval: 60_000 });
  useRealtime(['notification.created'], () => void invalidate('/notifications'));
  const unread = q.data?.unread ?? 0;

  const openItem = async (n: NotificationView) => {
    setOpen(false);
    if (!n.readAt) {
      await post(`/notifications/${n.id}/read`).catch(() => undefined);
      void invalidate('/notifications');
    }
    if (n.link) navigate(n.link);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative grid size-[34px] shrink-0 place-items-center rounded-md border border-border bg-surface text-text-2 hover:bg-surface-2 hover:text-text"
          aria-label={unread ? `Notifications, ${unread} unread` : 'Notifications'}
        >
          <Bell className="size-4" aria-hidden />
          {unread > 0 && <span className="absolute end-[7px] top-1.5 size-[7px] rounded-full border-2 border-surface bg-error" />}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" padding="none" className="w-[360px] overflow-hidden" aria-label="Notifications">
        <div className="flex items-center justify-between border-b border-border-2 px-3.5 py-2.5">
          <span className="text-13 font-semibold">Notifications</span>
          {unread > 0 && (
            <button
              type="button"
              className="text-12 font-medium text-primary-text hover:underline"
              onClick={async () => {
                await post('/notifications/read-all');
                void invalidate('/notifications');
              }}
            >
              Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[420px] overflow-auto">
          {(q.data?.items ?? []).length === 0 && <p className="px-3.5 py-6 text-center text-13 text-text-2">You’re all caught up.</p>}
          {(q.data?.items ?? []).map((n) => (
            <button
              key={n.id}
              type="button"
              onClick={() => void openItem(n)}
              className={cn(
                'flex w-full items-start gap-2.5 border-t border-border-2 px-3.5 py-2.5 text-start text-12 leading-[17px] first:border-t-0 hover:bg-surface-2',
                !n.readAt && 'bg-primary-soft/40',
              )}
            >
              <span className={cn('mt-[5px] size-2 shrink-0 rounded-full', n.readAt ? 'bg-border' : (NOTIF_TONE[n.kind] ?? 'bg-primary'))} />
              <span className="min-w-0 flex-1">
                <span className={cn('block', !n.readAt && 'font-medium')}>{n.title}</span>
                {n.body && <span className="block truncate text-text-2">{n.body}</span>}
                <span className="text-11 text-text-3">{relTime(n.createdAt)}</span>
              </span>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function ThemeButton() {
  const { resolvedTheme, setTheme } = useTheme();
  const next = resolvedTheme === 'dark' ? 'light' : 'dark';
  return (
    <IconButton
      aria-label={`Switch to ${next} theme`}
      tooltip={`Switch to ${next} theme`}
      variant="secondary"
      className="size-[34px]"
      onClick={() => {
        setTheme(next);
        void patch('/auth/prefs', { theme: next }).catch(() => undefined);
      }}
    >
      {resolvedTheme === 'dark' ? <Sun /> : <Moon />}
    </IconButton>
  );
}

function UserMenu() {
  const { me, isGlobal, logout } = useAuth();
  const navigate = useNavigate();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="inline-flex h-[34px] max-w-[280px] shrink-0 items-center gap-2 overflow-hidden rounded-md border border-border bg-surface pe-1.5 ps-1 text-text hover:bg-surface-2"
          aria-label={`Account: ${me.name}`}
        >
          <Avatar name={me.name} size="md" decorative />
          <span className="hidden max-w-[120px] truncate text-13 font-medium md:inline">{me.name}</span>
          <Badge tone={isGlobal ? 'critical' : 'primary'} size="xs" weight="semibold" className="whitespace-nowrap">
            {ROLE_LABELS[me.role]}
          </Badge>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[240px]">
        <DropdownMenuLabel className="font-normal">
          <span className="block text-13 font-medium text-text">{me.name}</span>
          <span className="block truncate text-12 text-text-2">{me.email}</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => navigate('/settings?tab=account')}>Account &amp; security</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => window.open('/api/docs', '_blank', 'noopener')}>API reference</DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate('/gallery')}>Design system</DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => void logout()}>Sign out</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function newFeedbackCount(counts: Record<string, Record<string, number>> | undefined): number {
  return counts?.status?.new ?? 0;
}

export function AppShell() {
  const { me, isGlobal, can } = useAuth();
  const { setTheme } = useTheme();
  // The theme saved on the account (PATCH /auth/prefs) wins over this browser's last choice, once per sign-in.
  useEffect(() => {
    if (me.theme) setTheme(me.theme);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.id]);
  const { navOpen, toggleNav, crumbs, openAi, setPaletteOpen } = useShell();
  const systemId = useScope((s) => s.systemId);
  const location = useLocation();
  const newFb = useGet<{ counts: Record<string, Record<string, number>> }>(
    `/feedback?status=new&limit=1${systemId ? `&systemId=${systemId}` : ''}`,
    { refetchInterval: 60_000 },
  );
  useRealtime(['feedback.created'], () => void invalidate('/feedback'));

  // ⌘K / Ctrl+K opens the command palette from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [setPaletteOpen]);

  const nav = useMemo<NavDef[]>(
    () =>
      [
        { key: 'dash', to: '/', label: 'Dashboard', icon: LayoutDashboard },
        { key: 'systems', to: '/systems', label: 'Systems', icon: Server },
        { key: 'library', to: '/library', label: 'Test library', icon: Library },
        { key: 'sessions', to: '/sessions', label: 'UAT sessions', icon: CalendarCheck, badge: newFeedbackCount(newFb.data?.counts) },
        { key: 'ai', label: 'AI assistant', icon: Sparkles, onClick: () => openAi(null), show: can('ai', 'read') },
        { key: 'users', to: '/users', label: 'Users & access', icon: Users, show: can('users', 'read') },
        { key: 'settings', to: '/settings', label: 'Settings', icon: Settings },
      ].filter((n) => n.show !== false),
    [can, newFb.data, openAi],
  );

  const scopeNote = isGlobal
    ? 'All systems · users & tenant settings'
    : `${me.systemGrants.length} ${me.systemGrants.length === 1 ? 'system' : 'systems'} · no user management`;

  return (
    <div className="flex h-dvh overflow-hidden bg-bg">
      <a href="#main" className="sr-only focus:not-sr-only focus:fixed focus:start-2 focus:top-2 focus:z-[100] focus:rounded-md focus:bg-surface focus:px-3 focus:py-2">
        Skip to content
      </a>
      <nav
        aria-label="Main"
        className={cn('flex shrink-0 flex-col overflow-hidden border-e border-border bg-surface transition-[width] duration-150', navOpen ? 'w-60' : 'w-16')}
      >
        <Link to="/" className="flex h-14 shrink-0 items-center gap-2.5 border-b border-border-2 px-3.5">
          <img src={logo} alt="" className="size-[26px] shrink-0 object-contain" />
          {navOpen && <span className="truncate font-semibold">UAT Proofing</span>}
          <span className="sr-only">UAT Proofing — dashboard</span>
        </Link>
        <div className="flex flex-1 flex-col gap-0.5 overflow-auto p-2">
          {nav.map((item) => (
            <NavItem key={item.key} item={item} open={navOpen} />
          ))}
        </div>
        <div className="flex shrink-0 flex-col gap-0.5 border-t border-border-2 p-2">
          {navOpen && (
            <div className="rounded-[6px] bg-surface-2 px-2.5 py-2 text-11 leading-[15px] text-text-2">
              <b className="text-text">{isGlobal ? 'Global admin' : 'Scoped access'}</b>
              <br />
              {scopeNote}
            </div>
          )}
          <button
            type="button"
            onClick={toggleNav}
            aria-label={navOpen ? 'Collapse navigation' : 'Expand navigation'}
            aria-expanded={navOpen}
            className="flex h-8 items-center gap-2.5 rounded-[6px] px-2.5 text-text-3 hover:bg-surface-2 hover:text-text"
          >
            <PanelLeft className="size-4 shrink-0" aria-hidden />
            {navOpen && <span className="text-12">Collapse</span>}
          </button>
        </div>
      </nav>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <header className="flex h-14 shrink-0 items-center gap-2.5 overflow-hidden border-b border-border bg-surface px-4">
          <Breadcrumb
            className="min-w-0 shrink truncate whitespace-nowrap"
            items={crumbs.map((c) => ({ label: c.label, href: c.to }))}
            renderLink={(item, props) => <Link to={item.href ?? '/'} {...props} />}
          />
          <span className="flex-[0_1_40px]" />
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            className="flex h-[34px] min-w-[60px] max-w-[360px] flex-[1_1_120px] items-center gap-2 overflow-hidden rounded-md border border-border bg-bg px-2.5 text-13 text-text-3 hover:border-text-3"
            aria-keyshortcuts="Meta+K Control+K"
          >
            <Search className="size-3.5 shrink-0" aria-hidden />
            <span className="flex-1 truncate text-start">Search cases, sessions, feedback…</span>
            <Kbd aria-hidden>⌘K</Kbd>
          </button>
          <ScopeMenu />
          <NotificationsButton />
          <ThemeButton />
          <UserMenu />
        </header>
        <main id="main" className="relative min-h-0 flex-1 overflow-auto" tabIndex={-1}>
          <Suspense fallback={<PageSpinner />}>
            <Outlet key={location.pathname.split('/').slice(0, 2).join('/')} />
          </Suspense>
        </main>
      </div>
      <CommandPalette />
      <AiAssistantDrawer />
    </div>
  );
}
